<template>
  <responsive-drawer-layout>
    <template v-slot:drawer>
      <tree-slider
        v-if="!dataLoadingInProcess"
        class="mt-0"
        :breadcrumb="fileBrowserPathResolved"
        :items="items"
        :itemsById="documentsById"
        :tags="tagsByInstance"
        :currentFolder="currentFolder"
        :foldersById="foldersById"
        :sorterName="sorterName"
        :hide-filter="hideDocumentFilter"
        root-text-key="documentsRootHeader"
        folderType="folder"
        :childrenType="['document', 'video']"
        @onPathChanged="onPathChanged"
      >
        <template v-slot:folder="{ folder }">
          <n-folder :folder="folder" :isUnreadCountFeatureEnabled="hasModuleUnreadCountFeatureEnabled" :badgeNumber="folder._unreadCount"></n-folder>
        </template>
        <template v-slot:item="{ item }">
          <base-list-item
            :id="`f_${item.id}`"
            :file="item"
            :isSelected="displayDocument && displayDocument.id === item.id"
            @onSelected="openSelectedDocument(item)"
            @toggleFavoriteState="toggleFavoriteState(item)"
            :isUnreadCountFeatureEnabled="hasModuleUnreadCountFeatureEnabled"
            :favoritesEnabled="hasModuleFavoritesEnabled"
            itemIcon="mdi-file-document-outline"
            favoriteIcon=""
            :markAsReadOnSelect="false"
          >
          </base-list-item>
        </template>
      </tree-slider>
    </template>
    <template v-slot:content>
      <pdf-js
        class="inherit-height scroll-container"
        :style="`margin-bottom:${isToolbarsHidden ? 0 : '64px'}`"
        v-if="!dataLoadingInProcess && displayDocument.type === 'document' && pdfJsDocumentUrl !== null && pdfViewer === 'PdfJs'"
        :docTitle="displayDocument.title"
        :url="pdfJsDocumentUrl"
        :page="$route.query.page"
        :search="$route.query.search"
        :download-file-name="downloadFileName"
        :hide-pdf-open-icon="hidePdfOpenIcon"
      ></pdf-js>
      <video-player v-if="displayDocument.type === 'video'" class="video-player" controls :src="pdfJsDocumentUrl" width="100%" height="500px"></video-player>
      <bogg-process-map v-else-if="hasLandingImage"></bogg-process-map>
      <empty-state-indicator v-else icon="mdi-text-box-search-outline" :text="$t('document.info.selected.none')" :subtitle="$t('chooseDocument')" />

      <!--<interactive-map-viewer
        v-if="hasInteractiveMapViewer && !dataLoadingInProcess"
        :interactiveMapViewer="getUpdatedDocumentRoutes"
        :interactiveMapViewerImage="interactiveMapViewerImage"
      />-->
    </template>
  </responsive-drawer-layout>
</template>

<script>
import { mapState } from 'vuex';
import { rCols } from '@/common/utils';
import common from '@/mixins/common';
import configMixin from '@/mixins/configMixin';

import PdfJs from '@/components/pdf/PdfJs';
import ResponsiveDrawerLayout from '@/layouts/ResponsiveDrawerLayout';

import index from './index';
import NFolder from '@/components/listItems/Folder';
import TreeSlider from '@/components/listItems/TreeSlider';
import BaseListItem from '@/components/listItems/BaseListItem.vue';
import readService from '@/services/readService';
import mandatoryToReadService from '@/services/mandatoryToReadService';
import acknowledgeService from '@/services/acknowledgeService';
import { ROUTE_NAMES } from './routes';
import EmptyStateIndicator from '@/components/container/EmptyStateIndicator.vue';
import uiHelpers from '@/common/uiHelpers';
import BoggProcessMap from '@/modules/document/boggProcessMap/BoggProcessMap';
import VideoPlayer from '@/components/VideoPlayer';

export default {
  name: 'DocumentsView',
  mixins: [common, configMixin],

  components: { BoggProcessMap, TreeSlider, BaseListItem, NFolder, PdfJs, ResponsiveDrawerLayout, EmptyStateIndicator, VideoPlayer },

  mounted() {
    this.initiate(this.$route);
  },
  data() {
    return {
      showPdf: false,
      pdfViewer: 'PdfJs',
      currentFolder: null,
      dataLoadingInProcess: true,
      fileBrowserPath: [],
      fileBrowserPathResolved: '',
    };
  },

  computed: {
    ...mapState('documents', [
      'instanceFolderTrees',
      'documentsById',
      'foldersById',
      'displayedDocumentId',
      'fileBrowserOpen',
      'dataLoaded',
      'tags',
      'documentSorterNamesByInstance',
      'documents',
    ]),
    instance() {
      return `${this.$route.params.instance}`;
    },
    sorterName() {
      console.debug('documentSorterNamesByInstance', JSON.stringify(this.$store.state.documents.documentSorterNamesByInstance, null, 2));
      return this.$store.state.documents.documentSorterNamesByInstance[this.instance];
    },
    confirmationType() {
      let { confirmationType } = this.moduleConfig;
      if (!confirmationType) {
        confirmationType = 'dialog';
      }
      return confirmationType;
    },

    items() {
      return this.instanceFolderTrees[this.$route.params.instance] ? this.instanceFolderTrees[this.$route.params.instance].root : [];
    },
    tagsByInstance(state) {
      // use == not === because == converts the variable values to the same type before performing comparison. This is called type coercion.
      return this.tags.filter((tag) => tag.instance == this.$route.params.instance).sort((a, b) => a.sort - b.sort);
    },
    pdfJsDocumentUrl() {
      if (this.documentsById) {
        let displayedDocument = this.documentsById[this.displayedDocumentId];
        if (displayedDocument) {
          // parameters separated by & because pdf.js uses the whole url as parameter
          // Token not used any longer as it is sent with cookie
          return this.constructPdfJsFileUrl(index.api, displayedDocument, 'filename');
        }
      }
      return null;
    },
    displayDocument() {
      return this.documentsById[this.displayedDocumentId] ?? {};
    },
    downloadFileName() {
      if (!this.displayDocument) return '';

      const { filename, title } = this.displayDocument;

      const extension = filename.split('.')[1];

      if (!extension) return '';

      return `${title}.${extension}`;
    },
    hasModuleFavoritesEnabled() {
      return this.isFavoritesEnabled(this.moduleType, this.$route.params.instance);
    },
    hasModuleUnreadCountFeatureEnabled() {
      return this.mx_isUnreadCountFeatureEnabled(this.moduleType, this.$route.params.instance);
    },
    moduleConfig() {
      return this.getModuleConfig(index.moduleType, this.$route.params.instance);
    },
    backgroundImg() {
      return this.moduleConfig.backgroundImg;
    },
    hasLandingImage() {
      return this.moduleConfig.hasLandingImage;
    },
    interactiveMapViewer() {
      return this.moduleConfig.interactiveMapViewer;
    },
    hasInteractiveMapViewer() {
      return 'interactiveMapViewer' in this.moduleConfig;
    },
    interactiveMapViewerImage() {
      return this.assetUrl(this.interactiveMapViewer.image);
    },
    hidePdfOpenIcon() {
      return this.moduleConfig.hidePdfOpenIcon !== undefined ? this.moduleConfig.hidePdfOpenIcon : false;
    },

    getUpdatedDocumentRoutes() {
      const mismatchedTitles = [];

      const updatedRoutes = this.interactiveMapViewer.areas.map((area) => {
        const document = this.getDocumentByTitle(area.title);
        if (!document) {
          mismatchedTitles.push(area.title);
        }
        return {
          ...area,
          path: document ? document._path : '#',
        };
      });

      if (mismatchedTitles.length > 0) {
        const errorMessage = `Konfiguration stimmt nicht überein für die Dokumente: ${mismatchedTitles.join(
          ', '
        )}. Bitte im Cockpit die Dokumententitel überprüfen.`;
        alert(errorMessage);
      }

      return updatedRoutes;
    },

    bgImgStyle() {
      if (this.backgroundImg) {
        let bgImgConf = rCols(this.backgroundImg[this.themeName], this.breakpoint.name);
        let isSvg = bgImgConf.img.indexOf('.svg') > 0;
        return {
          background: `url('${this.assetUrl(bgImgConf.img)}')`,
          height: '100%',
          minHeight: 'calc(100vh - 160px)',
          backgroundPosition: (bgImgConf.position ? bgImgConf.position : 'center center') + ' !important',
          backgroundSize: (bgImgConf.size ? bgImgConf.size : 'cover') + ' !important',
          backgroundOrigin: isSvg ? 'content-box' : 'initial',
          padding: isSvg ? '0% 25%' : 0,
          margin: isSvg ? 0 : 0,
        };
      }
      return {};
    },
    hideDocumentFilter() {
      return this.moduleConfig.hideDocumentFilter !== undefined ? this.moduleConfig.hideDocumentFilter : false;
    },
  },
  methods: {
    isUnread(document) {
      return readService.isUnread(document);
    },

    isMandatoryToReadAndNotRead(document) {
      return mandatoryToReadService.isMandatoryToReadAndNotRead(document);
    },
    isToAcknowledge(document) {
      return acknowledgeService.isToAcknowledge(document);
    },

    getDocumentByTitle(title) {
      return this.documents.find((doc) => doc.title === title);
    },
    onPathChanged(path) {
      console.debug('path', path);
      this.fileBrowserPath = path;
      let resolved = path
        .map((folderId) => {
          let folder = this.foldersById[folderId];
          return folder ? folder.name : '';
        })
        .join('/');
      this.fileBrowserPathResolved = resolved;
    },
    openSelectedDocument(item) {
      console.debug('open document with id', item.id, 'currently opened document has id', this.displayedDocumentId);
      this.mx_isLeftDrawerOpen = false;
      if (Number(this.displayedDocumentId) === item.id) {
        //this.$store.commit('documents/setFileBrowserOpen', { value: this.lgAndUp });
      } else if ((item.type === 'document' || item.type === 'video') && item.loadState !== 'LOAD_FAILED') {
        this.$router.push(`/documents/${this.$route.params.instance}/${item.id}`);
      }
    },
    toggleFavoriteState(item) {
      this.$store.dispatch('documents/toggleFavoriteState2', { item }, { root: true });
    },

    initiate(to, from) {
      console.debug('DocumentView initiate');
      if (!this.dataLoaded) {
        this.dataLoadingInProcess = true;
        return;
      }
      this.dataLoadingInProcess = false;

      let instance = Number(to.params.instance);
      let { documentId, folderId } = to.params;
      if (from) {
        let fromParams = from.params;
        if (fromParams && fromParams.instance === to.params.instance && fromParams.documentId === documentId && fromParams.folderId === folderId) {
          // console.debug(`Same path params as before instance=${instance}, documentId=${documentId}, folderId=${folderId}`);
          return;
        }
      }
      // // console.debug('Route name', to.name);
      switch (to.name) {
        case ROUTE_NAMES.documentsRoot:
          {
            this.$store.dispatch('documents/setDisplayedDocumentId', { documentId: null });

            if (this.hasInteractiveMapViewer && this.xs) {
              // close drawer in case of a landing page image
              this.mx_isLeftDrawerOpen = false;
            } else {
              this.mx_isLeftDrawerOpen = true;
            }

            this.fileBrowserPath = [];
            this.fileBrowserPathResolved = '';
            this.currentFolder = null;
          }
          break;
        case ROUTE_NAMES.documentFile:
          {
            let doc = this.documentsById[documentId];
            // console.debug('initiate documentFile route for ', documentId, doc);
            if (doc) {
              this.currentFolder = this.foldersById[doc.folderIdFk];

              // this.documentsById[document.id] && (this.documentsById[doc.id]._offline = true);
              // console.debug('********* documentView moved to document id', to.params.documentId);
              this.mx_isLeftDrawerOpen = false;
              this.$store.dispatch('documents/setDisplayedDocumentId', { documentId });

              uiHelpers.scrollToFileOfFileList(doc, 2000);
              uiHelpers.initiateDelayedMarkAsRead(this.$store.dispatch, doc, 1500);
            } else {
              //unknown doc, fallback documentRoot
              this.$store.dispatch('documents/setDisplayedDocumentId', { documentId: null });
              this.mx_isLeftDrawerOpen = true;
              this.fileBrowserPath = [];
              this.fileBrowserPathResolved = '';
              this.currentFolder = null;
            }
          }
          break;
        case ROUTE_NAMES.documentFolder: {
          this.currentFolder = this.foldersById[folderId];
        }
      }
    },
  },
  watch: {
    $route(to, from) {
      console.debug('Changed route from= ', from, 'to=', to);
      this.initiate(to, from);
    },
    dataLoaded(to, from) {
      this.initiate(this.$route);
    },
  },
};
</script>
